import { useQuery } from '@tanstack/react-query';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import i18next from 'i18next';
import type { GetStaticProps } from 'next';
import { Suspense, useEffect } from 'react';

import {
	getSignin,
	getSimpleFooter,
	GetSimpleFooterResponse,
} from 'services/idm';

import { useBrowsing } from 'hooks/use-browsing';

import { apiErrorHandler } from '@login/helpers';

import { FormBox } from '../../components/form-box';
import { SigninForm } from '../../patterns';

export type staticProps = {
	footerData: GetSimpleFooterResponse;
};

export const getStaticProps = (async (params) => {
	const locale = params?.locale as string;
	const { supportedLocales } = await getAppI18nConfig('login');
	await initI18n(locale, supportedLocales);
	const footerData = (await getSimpleFooter(locale)) || [];
	return { props: { footerData, locale } };
}) satisfies GetStaticProps<staticProps>;

const SignIn = () => {
	const { push } = useBrowsing();

	const { data: signinData } = useQuery({
		queryKey: ['signin', i18next.language],
		queryFn: () =>
			getSignin(i18next.language).catch((error) => {
				apiErrorHandler(error, i18next.language);
				throw error;
			}),
	});

	useEffect(() => {
		if (signinData?.action !== 'Redirect' || !signinData.redirectUrl)
			return;

		if (signinData.redirectUrl.startsWith('/connect')) {
			window.location.href = `${window.location.origin}${signinData.redirectUrl}`;
			return;
		}

		push(signinData.redirectUrl);
	}, [signinData, push]);

	return (
		<Suspense>
			{signinData?.action === 'Display' && (
				<FormBox>
					<SigninForm
						placeholders={signinData.placeholders}
						token={signinData.xsrfToken}
						userUnderAge={
							signinData.flow.contentId === 'user-under-age'
						}
					/>
				</FormBox>
			)}
		</Suspense>
	);
};

export default SignIn;
